import EarlyPayoutNotice from '@/components/feature/EarlyPayoutNotice';
import { formatMoney } from '@/utils/format';
import { balanceAfterAdvance } from '@/utils/invoice';
import type { PayeeInvoice, SinglePayee } from '@/types/portal';

interface ReconcilePanelProps {
  payee: SinglePayee;
  invoice: PayeeInvoice | null;
  currency: string;
  onCampaign: (id: string) => void;
}

/** Campaign reconciliation panel — nets off any early payout before the balance is paid. */
export default function ReconcilePanel({
  payee,
  invoice,
  currency,
  onCampaign,
}: ReconcilePanelProps) {
  if (payee.invoices.length === 0) {
    return (
      <div className="rounded-md border border-background-200 bg-background-100 px-3.5 py-3">
        <p className="text-xs text-foreground-500">
          One-off payment — no campaign invoice to reconcile against. Enter any amount.
        </p>
      </div>
    );
  }

  const money = currency || 'KES';
  const invoiceTotal = invoice?.amount ?? 0;
  const payable = balanceAfterAdvance(invoiceTotal, invoice?.advance);

  return (
    <div className="rounded-md border border-background-200 bg-background-100 px-3.5 py-3">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            {payee.invoices.length > 1 ? 'Reconcile from campaign' : 'Reconciled from invoice'}
          </span>
          {invoice && invoice.readiness === 'blocked' ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-100 px-2 py-0.5 text-[10px] font-label font-medium text-primary-800">
              <i className="ri-error-warning-line" />
              {invoice.issues[0] ?? 'Blocked'}
            </span>
          ) : null}
        </div>

        {payee.invoices.length > 1 ? (
          <select
            value={invoice?.campaignId ?? ''}
            onChange={(event) => onCampaign(event.target.value)}
            className="h-10 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
          >
            {payee.invoices.map((option) => (
              <option key={option.number} value={option.campaignId}>
                {option.campaign} · {option.number}
              </option>
            ))}
          </select>
        ) : null}

        {invoice ? (
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground-900">
                  {invoice.number} · {invoice.campaign}
                </p>
                <p className="text-xs text-foreground-500">Approved invoice</p>
              </div>
              <span className="text-sm font-medium text-foreground-950">
                {formatMoney(invoiceTotal, money)}
              </span>
            </div>

            {invoice.advance ? (
              <>
                <div className="flex items-center justify-between gap-2 border-t border-background-200 pt-2 text-xs">
                  <span className="inline-flex items-center gap-1.5 text-secondary-800">
                    <i className="ri-hand-coin-line" />
                    Early payout advance ({invoice.advance.percent}% · {invoice.advance.reference})
                  </span>
                  <span className="shrink-0 text-secondary-800">
                    − {formatMoney(invoice.advance.amount, money)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-background-200 pt-2 text-xs">
                  <span className="font-medium text-foreground-700">Balance payable</span>
                  <span className="font-medium text-foreground-950">
                    {formatMoney(payable, money)}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between border-t border-background-200 pt-2 text-xs">
                <span className="font-medium text-foreground-700">Balance payable</span>
                <span className="font-medium text-foreground-950">
                  {formatMoney(invoiceTotal, money)}
                </span>
              </div>
            )}

            {invoice.advance ? (
              <div className="pt-1">
                <EarlyPayoutNotice
                  advance={invoice.advance}
                  currency={money}
                  invoiceTotal={invoiceTotal}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}